import { useContext, useEffect, useState } from "react";
import { AppContext } from "../AppContextProvider";
import dealsService from "../services/dealsService";

const rowsOf = (resp) => {
  const rows = resp?.data?.data;
  return Array.isArray(rows) ? rows : [];
};

/**
 * Everything the deals map is built from: every lease/deed tract (location,
 * quartering, net acres), the lease/deeds that link tracts to deals, the
 * deals themselves (status, bonus, brokerage), and the `lease_deed_tracts` /
 * `lease_deeds` / `deals` field metadata for popup labels. Fetched once on mount.
 *
 * Same calls as the deals KPI header (listDeals with limit 5000,
 * listAllLeaseDeeds, listAllLeaseDeedTracts); row scoping for external
 * brokers is applied server-side on each.
 *
 * @returns {{ tracts: object[], leaseDeeds: object[], deals: object[],
 *   tractMetadata: object[], dealMetadata: object[],
 *   leaseDeedMetadata: object[], loading: boolean,
 *   errorMsg: string|null }}
 *
 * `enabled: false` fetches nothing, for a map whose page already holds this
 * data (the deals list's map band; see hooks/useDealsOverview).
 */
const useDealTracts = ({ enabled = true } = {}) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const oid = user?.idTokenClaims?.oid;

  const [tracts, setTracts] = useState([]);
  const [leaseDeeds, setLeaseDeeds] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tractMetadata, setTractMetadata] = useState([]);
  const [dealMetadata, setDealMetadata] = useState([]);
  const [leaseDeedMetadata, setLeaseDeedMetadata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!oid || !enabled) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setErrorMsg(null);
        let t = token;
        if (!t || (isTokenExpired && isTokenExpired(t))) t = await getToken();
        const args = { token: t, userOid: oid };
        const [trResp, ldResp, dealsResp, trMeta, dealMeta, ldMeta] =
          await Promise.allSettled([
            dealsService.listAllLeaseDeedTracts(args),
            dealsService.listAllLeaseDeeds(args),
            dealsService.listDeals({ ...args, filters: { limit: 5000 } }),
            dealsService.getMetadata({ ...args, tableName: "lease_deed_tracts" }),
            dealsService.getMetadata({ ...args, tableName: "deals" }),
            dealsService.getMetadata({ ...args, tableName: "lease_deeds" }),
          ]);
        if (cancelled) return;
        // Metadata is labels only — a failure there must not hide the data.
        if (trMeta.status === "fulfilled") setTractMetadata(rowsOf(trMeta.value));
        if (dealMeta.status === "fulfilled") setDealMetadata(rowsOf(dealMeta.value));
        if (ldMeta.status === "fulfilled") setLeaseDeedMetadata(rowsOf(ldMeta.value));
        for (const r of [trResp, ldResp, dealsResp]) {
          if (r.status === "rejected") throw r.reason;
        }
        setTracts(rowsOf(trResp.value));
        setLeaseDeeds(rowsOf(ldResp.value));
        setDeals(rowsOf(dealsResp.value));
      } catch (err) {
        if (!cancelled) setErrorMsg(err?.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Auth context is closed over; fetch once per signed-in user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid, enabled]);

  return {
    tracts,
    leaseDeeds,
    deals,
    tractMetadata,
    dealMetadata,
    leaseDeedMetadata,
    loading,
    errorMsg,
  };
};

export default useDealTracts;
